import { z } from "zod";

import { Frequency, Platforms, Stage } from "@/store/apis/adminApi.enhanced";

/**
 * Validación cliente del input de un Pop-up.
 *
 * Decisión de typing: no usamos `.default()` en el schema porque los
 * defaults rompen la inferencia de output types cuando se combinan con
 * `superRefine`. Los valores por defecto se aplican en `useForm` →
 * `defaultValues`. Esto deja el `PopupInput` como el shape exacto que
 * esperamos del form.
 */
export const PopupInputSchema = z
  .object({
    title: z.string().trim().max(80, "Máximo 80 caracteres"),
    body: z.string().trim().max(200, "Máximo 200 caracteres"),
    image_url: z
      .string()
      .trim()
      .refine((v) => !v || /^https?:\/\//.test(v), "Debe ser una URL válida"),
    cta_text: z.string().trim().max(40, "Máximo 40 caracteres"),
    cta_url: z
      .string()
      .trim()
      .refine(
        (v) => !v || /^(arya:\/\/|https:\/\/)/.test(v),
        "Debe comenzar con https:// o arya://",
      ),
    target_stages: z.array(z.nativeEnum(Stage)),
    platforms: z.array(z.nativeEnum(Platforms)).min(1, "Elige al menos una plataforma"),
    frequency: z.nativeEnum(Frequency),
    priority: z
      .number({ invalid_type_error: "Debe ser un número" })
      .int("Debe ser un entero")
      .min(0, "No puede ser negativo")
      .max(100, "Máximo 100"),
    start_at: z.string().trim(),
    end_at: z.string().trim(),
    active: z.boolean(),
  })
  .superRefine((val, ctx) => {
    if (!val.title && !val.body) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["title"],
        message: "Agrega al menos un título o un texto",
      });
    }
    if (val.cta_text && !val.cta_url) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["cta_url"],
        message: "Si pones texto del botón, define el enlace",
      });
    }
    if (val.cta_url && !val.cta_text) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["cta_text"],
        message: "Si pones un enlace, define el texto del botón",
      });
    }
    if (val.start_at && val.end_at) {
      const start = new Date(val.start_at).getTime();
      const end = new Date(val.end_at).getTime();
      if (Number.isFinite(start) && Number.isFinite(end) && end <= start) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["end_at"],
          message: "La fecha fin debe ser posterior al inicio",
        });
      }
    }
  });

export type PopupInput = z.infer<typeof PopupInputSchema>;

/** Mapea el input del form al body del back. */
export function toPopupApiBody(input: PopupInput) {
  return {
    title: input.title.trim() || undefined,
    body: input.body.trim() || undefined,
    image_url: input.image_url.trim() || null,
    cta_text: input.cta_text.trim() || null,
    cta_url: input.cta_url.trim() || null,
    target_stages: input.target_stages,
    platforms: input.platforms,
    frequency: input.frequency,
    priority: input.priority,
    start_at: input.start_at ? new Date(input.start_at).toISOString() : null,
    end_at: input.end_at ? new Date(input.end_at).toISOString() : null,
    active: input.active,
  };
}

/** ISO → `datetime-local` para hidratar `<input>`. */
export function isoToDatetimeLocal(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
